/**
 * Conferência pública de fechamento (magic-link, sem login).
 * Cliente revisa os atendimentos do mês, desmarca o que não reconhece e confirma.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, FileCheck2, Loader2, ShieldCheck } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { publicClosingApi, type PublicClosing } from "@/lib/api-public";
import { formatCurrency, formatDate } from "@/lib/format";

const MONTHS = [
  "", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const CLOSED = new Set(["closed_by_client", "adjusted", "finalized"]);

export function ClosingReviewPublicPage() {
  const { token = "" } = useParams();
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["public-closing", token],
    queryFn: () => publicClosingApi.get(token),
    retry: false,
  });

  // estado local: itens reconhecidos + razões de contestação
  const [recognized, setRecognized] = useState<Record<string, boolean>>({});
  const [reasons, setReasons] = useState<Record<string, string>>({});
  const [name, setName] = useState("");

  useEffect(() => {
    if (q.data) {
      setRecognized(Object.fromEntries(q.data.items.map((i) => [i.id, i.recognized])));
      setReasons(Object.fromEntries(q.data.items.map((i) => [i.id, i.dispute_reason ?? ""])));
    }
  }, [q.data]);

  const totals = useMemo(() => {
    const items = q.data?.items ?? [];
    let rec = 0;
    let disp = 0;
    for (const it of items) {
      const amt = parseFloat(it.amount);
      if (recognized[it.id] ?? true) rec += amt;
      else disp += amt;
    }
    return { rec, disp, gross: rec + disp, disputedCount: items.filter((i) => !(recognized[i.id] ?? true)).length };
  }, [q.data, recognized]);

  const confirm = useMutation({
    mutationFn: async () => {
      const disputed = (q.data?.items ?? [])
        .filter((i) => !(recognized[i.id] ?? true))
        .map((i) => ({ item_id: i.id, reason: reasons[i.id] || null }));
      await publicClosingApi.review(token, disputed);
      return publicClosingApi.confirm(token, name || null);
    },
    onSuccess: (data) => {
      qc.setQueryData(["public-closing", token], data);
      toast.success("Fechamento confirmado! Obrigado.");
    },
    onError: () => toast.error("Não foi possível confirmar", { description: "Tente novamente." }),
  });

  if (q.isLoading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-sky-500" />
      </div>
    );
  }

  if (q.isError || !q.data) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50 p-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-bold text-slate-800">Link inválido ou expirado</h1>
          <p className="mt-2 text-slate-500">
            Solicite um novo link de conferência ao time EuNerd.
          </p>
        </div>
      </div>
    );
  }

  const c: PublicClosing = q.data;
  const periodo = `${MONTHS[c.month]}/${c.year}`;
  const isClosed = CLOSED.has(c.status);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <div className="mx-auto max-w-3xl px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-2xl bg-sky-500 text-white">
            <FileCheck2 className="h-6 w-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold leading-tight">Conferência de Faturamento</h1>
            <p className="text-sm text-slate-500">
              {c.client_name} · {periodo}
            </p>
          </div>
        </div>

        {isClosed ? (
          <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-emerald-800">
            <div className="flex items-center gap-2 font-semibold">
              <CheckCircle2 className="h-5 w-5" /> Fechamento confirmado
            </div>
            <p className="mt-1 text-sm">
              Valor reconhecido: <b>{formatCurrency(c.recognized_total)}</b>
              {parseFloat(c.disputed_total) > 0 && (
                <> · contestado: {formatCurrency(c.disputed_total)}</>
              )}
              . O time EuNerd dará sequência ao faturamento.
            </p>
          </div>
        ) : (
          <p className="mt-4 text-sm text-slate-600">
            Revise os atendimentos abaixo. <b>Desmarque</b> os que você não reconhece (pode
            informar o motivo) e <b>confirme</b> para fechar o mês.
          </p>
        )}

        {/* Itens */}
        <div className="mt-6 space-y-2">
          {c.items.map((it) => {
            const ok = recognized[it.id] ?? true;
            return (
              <div
                key={it.id}
                className={`rounded-xl border p-3 transition-colors ${
                  ok ? "border-slate-200 bg-white" : "border-rose-200 bg-rose-50"
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    className="mt-1 h-5 w-5 shrink-0 accent-sky-600"
                    checked={ok}
                    disabled={isClosed}
                    onChange={(e) =>
                      setRecognized((r) => ({ ...r, [it.id]: e.target.checked }))
                    }
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{it.description}</span>
                      <span className="font-semibold tabular-nums whitespace-nowrap">
                        {formatCurrency(it.amount)}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500">
                      {it.job_code}
                      {it.scheduled_at && <> · {formatDate(it.scheduled_at)}</>}
                    </div>
                    {!ok && !isClosed && (
                      <input
                        type="text"
                        placeholder="Motivo (opcional)"
                        value={reasons[it.id] ?? ""}
                        onChange={(e) =>
                          setReasons((r) => ({ ...r, [it.id]: e.target.value }))
                        }
                        className="mt-2 w-full rounded-lg border border-rose-200 bg-white px-3 py-1.5 text-sm outline-none focus:border-rose-400"
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Resumo + confirmar */}
        <div className="sticky bottom-0 mt-6 rounded-2xl border border-slate-200 bg-white/90 p-4 backdrop-blur">
          <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
            <div className="flex gap-4">
              <span>
                Reconhecido: <b className="text-emerald-600">{formatCurrency(totals.rec)}</b>
              </span>
              {totals.disputedCount > 0 && (
                <span>
                  Contestado:{" "}
                  <b className="text-rose-600">{formatCurrency(totals.disp)}</b> (
                  {totals.disputedCount})
                </span>
              )}
            </div>
            <span className="text-slate-500">Total: {formatCurrency(totals.gross)}</span>
          </div>

          {!isClosed && (
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <input
                type="text"
                placeholder="Seu nome (para registro)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400"
              />
              <Button
                onClick={() => confirm.mutate()}
                disabled={confirm.isPending}
                className="bg-sky-600 hover:bg-sky-700"
              >
                {confirm.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <ShieldCheck className="h-4 w-4" />
                )}
                Confirmar fechamento
              </Button>
            </div>
          )}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">EuNerd · Operações</p>
      </div>
    </div>
  );
}
